import { useCallback, useEffect, useState } from 'react';

import { useIsMobile } from './use-is-mobile';

interface MobileOverlay {
  isMobile: boolean;
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

/**
 * Open/closed state for something that only exists in the mobile layout — the
 * side menu's drawer, the header's full-width search.
 *
 * These all share one rule that is easy to forget: the state is meaningless
 * above `md`, because the affordance that opens it is replaced by its desktop
 * equivalent. Left alone, an overlay opened on a phone comes back the next time
 * the viewport narrows. This closes it on the way out and reports `isOpen` as
 * false above `md` regardless, so a caller cannot render a mobile sheet over a
 * desktop layout even for the render the reset lands on.
 */
export function useMobileOverlay(): MobileOverlay {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      setIsOpen(false);
    }
  }, [isMobile]);

  // Only mobile can open it. The reset effect fires on leaving `md`, not on
  // entering it, so a stray open() above `md` would sit in state and surface the
  // moment the viewport narrowed.
  const open = useCallback(() => {
    if (isMobile) {
      setIsOpen(true);
    }
  }, [isMobile]);
  const close = useCallback(() => setIsOpen(false), []);

  return { isMobile, isOpen: isMobile && isOpen, open, close };
}
